import { Button, Form, Input, message, Modal, Select } from "antd";
import React, { useEffect, useState } from "react";
import Service from "../../services";

const WALLET_DISPLAY_NAME = {
  spot: "Fiat and Spot",
  future: "USD-M Futures",
  delivery: "COIN-M Futures",
};

const TransferButton = ({ exchangeId, account }) => {
  const [form] = Form.useForm();
  const code = Form.useWatch("code", form);
  const from = Form.useWatch("from", form);
  const to = Form.useWatch("to", form);
  const amount = Form.useWatch("amount", form);
  const [isModalOpen, setModalOpen] = useState(false);
  const [balance, setBalance] = useState({});

  const layout = {
    labelCol: { span: 6 },
    wrapperCol: { span: 16 },
  };

  useEffect(() => {
    if (isModalOpen) {
      form.setFieldsValue({
        exchangeId,
        account,
      });
    }
  }, [form, exchangeId, account, isModalOpen]);

  useEffect(() => {
    async function listWalletBalances(exchangeId, account) {
      try {
        const response = await Service.listWalletBalances(exchangeId, account);
        //console.log(response);
        setBalance(response);
      } catch (error) {
        console.error(`Error fetching balances: ${error.message}`);
      }
    }
    if (isModalOpen) {
      listWalletBalances(exchangeId, account);
    }
  }, [account, exchangeId, isModalOpen]);

  const handleModalOnOk = () => {
    form
      .validateFields()
      .then((formParams) => {
        return Service.transferWalletFund({ ...formParams });
      })
      .then((response) => {
        if (!response || typeof response !== "string") {
          //console.log({ response });
          throw new Error(`Something is wrong: response is malformed`);
        }
        setModalOpen(false);
        form.resetFields();
        message.success(response);
      })
      .catch((error) => {
        console.log("Failed:", error);
        if (error.errorFields) {
          message.error(error.errorFields.map((field) => field.errors).join(","));
        } else {
          message.error("Transfer failed. The coin may not be transferred into the wallet.");
        }
      });
  };

  const handleModalOnCancel = () => {
    form.resetFields();
    setModalOpen(false);
  };

  const handleButtonOnClick = () => {
    setModalOpen(true);
  };

  const getAssetBalance = (code) => {
    const asset = balance[from]?.find((item) => item.asset === code);
    return asset?.maxWithdrawAmount || "0";
  };

  const validateTransfer = () => {
    if (!to || !from || from === to) {
      return Promise.reject("Please select valid transfer from and transfer to account");
    }
    if ((from === "future" && to === "delivery") || (from === "delivery" && to === "future")) {
      return Promise.reject(`Transfer can't be made between ${from} and ${to}`);
    }
    return Promise.resolve();
  };

  const validateAmount = () => {
    if (amount && isNaN(amount)) {
      return Promise.reject("Please input a valid number");
    } else if (parseFloat(getAssetBalance(code)) && parseFloat(amount) > parseFloat(getAssetBalance(code))) {
      return Promise.reject("Transfer amount exceeds available balance.");
    } else {
      return Promise.resolve();
    }
  };

  const getModalContent = () => {
    return (
      <Form
        form={form}
        {...layout}
        initialValues={{
          exchangeId,
          account,
        }}
      >
        <Form.Item name="exchangeId" label="Exchange">
          <Input disabled={true} />
        </Form.Item>
        <Form.Item name="account" label="Account">
          <Input disabled={true} />
        </Form.Item>
        <Form.Item name="from" label="Transfer From" rules={[{ required: true, message: "Please select an account" }]}>
          <Select showSearch placeholder="Transfer From">
            {Object.entries(WALLET_DISPLAY_NAME).map(([accountType, accountTypeDisplayName]) => (
              <Select.Option key={accountType} value={accountType}>
                {accountTypeDisplayName}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="to" label="Transfer To" dependencies={["from"]} rules={[{ required: true }, { validator: validateTransfer }]}>
          <Select showSearch placeholder="Transfer To">
            {Object.entries(WALLET_DISPLAY_NAME).map(([key, value]) => (
              <Select.Option key={key} value={key}>
                {value}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="code" label="Coin" dependencies={["from", "to"]} rules={[{ required: true, message: "Please select a coin" }]}>
          <Select showSearch placeholder="Coin">
            {balance[from]
              ?.map(({ asset }) => asset)
              .map((asset) => (
                <Select.Option key={asset} value={asset}>
                  {asset}
                </Select.Option>
              ))}
          </Select>
        </Form.Item>
        <Form.Item label="Balance" name="Balance">
          {getAssetBalance(code)}
        </Form.Item>
        <Form.Item
          label="Amount to transfer"
          name="amount"
          rules={[{ required: true, message: "Please input the amount" }, { validator: validateAmount }]}
        >
          <Input type="number" placeholder="Enter amount" />
        </Form.Item>
      </Form>
    );
  };

  return (
    <div style={{ marginLeft: "auto", paddingRight: "20px" }}>
      <Button type="secondary" style={{ marginLeft: 8 }} onClick={handleButtonOnClick}>
        Transfer Assets
      </Button>
      <Modal
        width={640}
        title="Transfer"
        okText="Transfer"
        cancelText="Cancel"
        open={isModalOpen}
        onOk={handleModalOnOk}
        onCancel={handleModalOnCancel}
      >
        {getModalContent()}
      </Modal>
    </div>
  );
};

export default TransferButton;
